import React, { useEffect } from "react";
import envConfig from "src/envConfig/envHelper";
import { camelCase } from "lodash";

const isSuggestCssSelectorsEnabled =
  envConfig.windowRuntime?.suggestCssSelectors;

declare global {
  interface Window {
    suggestCssSelectors?: (
      givenSelector: string,
      vipAttributes: string[],
      ignoreAttributeValues: string[],
      vipParentSelectors: string[]
    ) => void;
    suggestCssSelectorsForInteractives?: () => void;
  }
}

function getVipSelector(
  el: Element,
  vipAttrs: string[],
  ignoreValues: string[]
): string | null {
  const tag = el.tagName.toLowerCase();
  const parts: string[] = [];
  for (const attr of vipAttrs) {
    if (el.hasAttribute(attr)) {
      const value = el.getAttribute(attr);
      if (value && !ignoreValues.includes(value)) {
        parts.push(`[${attr}="${value}"]`);
      }
    }
  }

  return parts.length > 0 ? `${tag}${parts.join("")}` : null;
}

function matchesAnySelector(element: Element, selectors: string[]): boolean {
  return selectors.some((sel) => element.matches(sel));
}

function buildSelectorPath(
  el: Element,
  vipAttrs: string[],
  ignoreValues: string[],
  vipSelectors: string[]
): string {
  const path: string[] = [];
  let current = el;
  let isFirst = true;
  while (
    current &&
    current.nodeType === 1 &&
    current.tagName.toLowerCase() !== "html"
  ) {
    if (isFirst) {
      // Always include the target element itself
      const targetSelector = getVipSelector(current, vipAttrs, ignoreValues);
      if (targetSelector) path.unshift(targetSelector);
      isFirst = false;
    } else if (matchesAnySelector(current, vipSelectors)) {
      // Only include parent if it matches vipParentSelectors
      const vipSelector = getVipSelector(current, vipAttrs, ignoreValues);
      if (vipSelector) path.unshift(vipSelector);
    }
    if (!current.parentElement) break;
    current = current.parentElement;
  }
  return path.join(" ");
}

function extractAllAttrValues(segment: string): string[] {
  const matches = [...segment.matchAll(/\[([a-zA-Z0-9-_]+)="([^"]+)"\]/g)];
  return matches.map((m) => m[2]);
}

export const ExposeCssSelectorSuggestion: React.FC = () => {
  useEffect(() => {
    if (isSuggestCssSelectorsEnabled) {
      window.suggestCssSelectors = (
        givenSelector: string,
        vipAttributes: string[],
        ignoreAttributeValues: string[],
        vipParentSelectors: string[]
      ): void => {
        const elements = document.querySelectorAll(givenSelector);

        elements.forEach((el) => {
          const selector = buildSelectorPath(
            el,
            vipAttributes,
            ignoreAttributeValues,
            vipParentSelectors
          );
          try {
            const uniqueness = document.querySelectorAll(selector).length === 1;
            if (!uniqueness) {
              console.log("❌", el);
              throw new Error("not usable");
            }
            const segments = selector.split(" ");
            const allValues: string[] = [];
            segments.forEach((seg) => {
              extractAllAttrValues(seg).forEach((val) => {
                if (!allValues.includes(val)) {
                  allValues.push(val);
                }
              });
            });
            // Convert each value to PascalCase and join with _
            let varName = allValues.map(camelCase).join("_");
            if (el.tagName === "BUTTON" && el.textContent)
              varName += `_${camelCase(el.textContent.trim())}`;
            console.log("✅", `const ${varName} = '${selector}';`, el);
          } catch (e) {}
        });
      };
      window.suggestCssSelectorsForInteractives = () =>
        window.suggestCssSelectors &&
        window.suggestCssSelectors(
          "input, select, textarea:not([readonly]), li[role='option'], button[type='button']",
          [
            "name",
            "data-id",
            "value",
            "id",
            "data-option-index",
            "title",
            "aria-label",
          ],
          [],
          [
            "section",
            "main",
            "article",
            "aside",
            "nav",
            "form",
            "details",
            "dialog",
            "header",
            "footer",
            "div[role='presentation'][id]",
            "div[data-role='section'][id]",
          ]
        );
    }

    return () => {
      delete window.suggestCssSelectors;
      delete window.suggestCssSelectorsForInteractives;
    };
  }, []);

  return <></>;
};
